import React from "react";
import { Button } from "@/components/Button";
import { SvgSrcIcon } from "@/components/SvgSrcIcon";
import SiteNavLink from "./SiteNavLink";
import { NavCard } from "./NavCard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type NavIcon = {
  svg: string;
  title?: string;
};

export type NavCard = {
  href: string;
  title: string;
  description: string;
  image: NavIcon;
};

export type Link = {
  href: string;
  title: string;
  bold?: boolean;
};

export type SubMenu = {
  title: string;
  links: NavData[];
};

export type SubMenuType = {
  type: "submenu";
} & SubMenu;

export type NavData =
  | {
      type: "link" | "button";
      href: string;
      title: string;
    }
  | SubMenuType
  | {
      type: "card-grid" | "stacked-list";
      title?: string;
      button: {
        href: string;
        title: string;
      };
      links: NavCard[];
    }
  | {
      type: "link-list";
      title?: string;
      button?: {
        href: string;
        title: string;
      };
      links: Link[];
    }
  | {
      type: "divider";
    };

export type NavItemProps =
  | Exclude<NavData, SubMenuType>
  | ({
      onSubmenuClick: () => void;
    } & SubMenuType);

const NavItem = (navItem: NavItemProps) => {
  switch (navItem.type) {
    case "link": {
      return (
        <Button
          component="a"
          href={navItem.href}
          size="lg"
          className="btn-ghost -mx-8 -my-4 flex justify-between rounded-none border-0 px-8 py-4"
        >
          {navItem.title}
        </Button>
      );
    }
    case "submenu": {
      return (
        <Button
          onClick={() => navItem.onSubmenuClick()}
          size="lg"
          className="btn-ghost -mx-8 -my-4 flex justify-between rounded-none border-0 px-8 py-4"
        >
          {navItem.title}
          <ChevronRightIcon className="-mr-1.5 text-primary" />
        </Button>
      );
    }
    case "button": {
      return (
        <Button
          component="a"
          size="lg"
          href={navItem.href}
          className="my-0 w-full"
        >
          {navItem.title}
        </Button>
      );
    }
    case "link-list": {
      return (
        <div className="grid lg:block">
          {navItem.title && (
            <h3 className="subtitle mb-4 text-md font-semibold">
              {navItem.title}
            </h3>
          )}
          {navItem.links?.map((childLink: Link) => (
            <SiteNavLink
              href={childLink.href}
              title={childLink.title}
              key={childLink.title}
              bold={childLink.bold}
            />
          ))}
          {navItem.button ? (
            <Button
              className="mt-4 w-full"
              href={navItem.button.href}
              aria-label={navItem.button.title}
              component="a"
              size="lg"
            >
              {navItem.button.title}
            </Button>
          ) : null}
        </div>
      );
    }
    case "card-grid": {
      return (
        <div className="my-0 lg:text-center">
          {navItem.title && (
            <h5 className="subtitle mb-6 text-md font-semibold">
              {navItem.title}
            </h5>
          )}
          <div className="flex space-x-4">
            {navItem.links?.map((childLink: NavCard) => (
              <NavCard
                className="flex-1"
                key={childLink.title}
                title={childLink.title}
                description={childLink.description}
                href={childLink.href}
                image={{ src: childLink.image.svg, height: 72, width: 72 }}
              />
            ))}
          </div>
          <Button
            className="mt-6 w-full lg:w-auto"
            href={navItem.button.href}
            aria-label={navItem.button.title}
            component="a"
            size="lg"
          >
            {navItem.button.title}
          </Button>
        </div>
      );
    }
    case "stacked-list": {
      return (
        <div className="grid lg:block">
          {navItem.title && (
            <h5 className="subtitle mb-2 text-md font-semibold">
              {navItem.title}
            </h5>
          )}
          {navItem.links?.map((childLink: NavCard, index: number) => {
            return (
              <div key={childLink.title}>
                {index !== 0 && (
                  <hr className="mx-0 my-0 w-full border-primary-200" />
                )}
                <a
                  href={childLink.href}
                  className="group -mx-8 flex px-8 py-4 transition duration-300"
                >
                  <SvgSrcIcon
                    width={48}
                    height={48}
                    src={childLink.image.svg}
                    title={childLink.image.title}
                    className="svg-icon basis-2/12"
                  ></SvgSrcIcon>
                  <div className="grow basis-10/12 text-left">
                    <div className="ml-4">
                      <h5 className="subtitle mb-2 font-semibold text-primary">
                        {childLink.title}
                      </h5>
                      {childLink.description && (
                        <p className="body text-sm group-hover:text-primary">
                          {childLink.description}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              </div>
            );
          })}
          <Button
            className="mt-2 w-full"
            href={navItem.button.href}
            aria-label={navItem.button.title}
            component="a"
            size="lg"
          >
            {navItem.button.title}
          </Button>
        </div>
      );
    }
    case "divider": {
      return <hr className="mx-0 my-0 w-full border-primary-200" />;
    }
  }
};

export { NavItem };
